import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Users, Sparkles, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [userCount, setUserCount] = useState(0);
  const featuresRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/waitlist/count`)
      .then(res => setUserCount(res.data.count))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0EE] overflow-x-hidden" data-testid="landing-page">
      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="text-xl font-black tracking-[-0.05em]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            VAULT
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
              className="text-sm font-medium text-[#888] hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register')}
              data-testid="nav-signup-btn"
              className="text-sm font-bold bg-[#FF3B30] text-white px-4 py-2 border border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all"
            >
              Sign Up Free
            </button>
            <button
              onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' })}
              className="text-sm font-medium text-[#888] hover:text-white transition-colors hidden sm:block"
            >
              Features
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="min-h-screen flex items-center justify-center relative px-6 pt-16">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[20%] left-[10%] w-72 h-72 bg-[#FF3B30]/5 blur-[100px] animate-pulse" />
          <div className="absolute bottom-[20%] right-[10%] w-96 h-96 bg-[#007AFF]/5 blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-[50%] left-[50%] w-64 h-64 bg-[#FFD60A]/3 blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#333] mb-8 text-sm" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <div className="w-2 h-2 bg-[#30D158] rounded-full animate-pulse" />
            <span className="text-[#888] font-mono text-xs tracking-wider uppercase">
              {userCount > 0 ? `${userCount}+ users joined` : 'Now Open'}
            </span>
          </div>

          {/* Main headline */}
          <h1
            className="text-5xl sm:text-7xl lg:text-[5.5rem] font-black leading-[0.9] tracking-[-0.04em] mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif", animation: 'fadeInUp 0.6s ease-out 0.1s both' }}
          >
            Your ideas<br />
            <span className="relative">
              deserve better
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 400 12" fill="none">
                <path d="M2 10C100 2 300 2 398 10" stroke="#FF3B30" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
            <br />
            <span className="text-[#555]">than a notes app.</span>
          </h1>

          <p
            className="text-lg sm:text-xl text-[#888] max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}
          >
            VAULT captures, organizes, and supercharges your ideas with AI.
            Stop losing your best thoughts to scattered docs and forgotten tabs.
          </p>

          {/* CTA Buttons */}
          <div style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }} className="max-w-md mx-auto">
            <button
              onClick={() => navigate('/register')}
              data-testid="hero-signup-btn"
              className="w-full bg-[#FF3B30] text-white font-bold px-8 py-4 border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 text-sm tracking-wider uppercase flex items-center justify-center gap-2 mb-3"
            >
              Get Started Free <ArrowRight size={16} />
            </button>
            <p className="text-[#555] text-xs">Unlimited ideas. 20 AI enhancements/month. No credit card required.</p>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce pointer-events-none">
            <ChevronDown size={24} className="text-[#555]" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section ref={featuresRef} className="py-24 px-6 border-t border-[#222]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-5xl font-black tracking-[-0.03em] mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Built for thinkers.
            </h2>
            <p className="text-[#888] text-lg max-w-xl mx-auto">
              Not another notes app. A system designed for how ideas actually work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {[
              {
                icon: <Sparkles size={24} />,
                title: 'AI Enhancement',
                desc: 'GPT-5.2 expands your rough thoughts into detailed, actionable ideas. One click.',
                color: '#FF3B30'
              },
              {
                icon: <Zap size={24} />,
                title: 'Instant Capture',
                desc: 'Idea templates, daily prompts, and keyboard shortcuts. Capture in under 3 seconds.',
                color: '#FFD60A'
              },
              {
                icon: <Shield size={24} />,
                title: 'Organized Chaos',
                desc: 'Categories, tags, search, and archive. Find any idea instantly, even from months ago.',
                color: '#007AFF'
              },
              {
                icon: <Users size={24} />,
                title: 'Team Boards',
                desc: 'Share ideas with your team. Invite members, assign roles, track activity.',
                color: '#34C759'
              },
              {
                icon: <div className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>PWA</div>,
                title: 'Works Everywhere',
                desc: 'Install on any device. Works offline. Your pocket idea vault.',
                color: '#AF52DE'
              },
              {
                icon: <div className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$0</div>,
                title: 'Free to Start',
                desc: 'Unlimited ideas and 20 AI enhancements per month. No credit card needed.',
                color: '#FF9500'
              }
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-[#0A0A0A] p-8 lg:p-10 hover:bg-[#111] transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-5" style={{ color: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-black mb-2 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-[#888] text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-20 px-6 border-t border-[#222]">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl sm:text-5xl font-black text-[#FF3B30]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {userCount || '0'}+
              </div>
              <div className="text-[#888] text-sm mt-1 font-mono uppercase tracking-wider">Users Joined</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-black text-[#007AFF]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                3s
              </div>
              <div className="text-[#888] text-sm mt-1 font-mono uppercase tracking-wider">To Capture</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-black text-[#FFD60A]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                AI
              </div>
              <div className="text-[#888] text-sm mt-1 font-mono uppercase tracking-wider">Powered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video */}
      <section className="py-20 sm:py-28 px-6 border-t border-[#222]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-3xl sm:text-4xl font-black tracking-[-0.03em] mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Watch the tour
            </h2>
            <p className="text-[#888] text-base max-w-md mx-auto">
              See VAULT in action — capture, organize, and enhance ideas in under a minute.
            </p>
          </div>
          <div className="border-2 border-[#333] bg-[#111] relative">
            <video
              data-testid="demo-video"
              controls
              preload="metadata"
              playsInline
              poster=""
              className="w-full block"
              style={{ aspectRatio: '16/9' }}
            >
              <source src="/videos/vault-demo-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 border-t border-[#222]">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-5xl font-black tracking-[-0.03em] mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Stop losing<br />brilliant ideas.
          </h2>
          <p className="text-[#888] text-lg mb-8 max-w-lg mx-auto">
            Sign up free. Capture every idea. Enhance with AI.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-[#0A0A0A] font-bold px-10 py-4 border-2 border-white hover:bg-transparent hover:text-white transition-all duration-300 text-sm tracking-wider uppercase"
            data-testid="bottom-cta-btn"
          >
            Create Your Vault
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#222]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm font-black tracking-[-0.05em]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            VAULT
          </div>
          <div className="flex items-center gap-6 text-[#555] text-xs">
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button>
            <button onClick={() => navigate('/blog')} className="hover:text-white transition-colors">Blog</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button>
            <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
